import axios from 'axios';
import { NO_COMPANY_SCOPE_REQUEST_CONFIG } from '../../hooks/dashboard';

/**
 * ROAR-4851 (ROAR-4802 / P1.11, PR1, api-backend-provapt) — GET /team/members (AC7).
 *
 * Which roster comes back is decided entirely server-side by the caller's own tier — a
 * company-tier caller (`customer_admin`/`customer_user`) gets their own company's roster; an
 * MSSP-tier caller (`mssp_admin`/`mssp_user`) gets their own MSSP's staff roster by default,
 * NEVER a managed company's members even if a `selected_company_uuid` is present. No user or
 * company identifier is accepted from the request, so — same rationale as `getLoginActivity`
 * (ROAR-4942) — `NO_COMPANY_SCOPE_REQUEST_CONFIG` strips the `company_id`/`selected_company_uuid`
 * headers `src/index.js` sets at bootstrap; sending them would be misleading, since the backend
 * ignores them for this endpoint regardless.
 *
 * Backend follow-up (unreleased as of this comment, branches `feature/team-members-invite-id` +
 * `feature/mssp-managed-company-roster` → `feature/ROAR-4802v2`, no JIRA sub-task): an MSSP-tier
 * caller can opt into a managed company's roster instead via `?scope=company` — requires
 * `selected_company_uuid` (400s without it), so NOT wired here since this query deliberately never
 * sends that header; a future "roster of the companies I manage" view would need its own query
 * function using the standard scoped pattern, not this one.
 *
 * Response item shape (confirmed against the shipped handler, `get.listMembers.js` +
 * `teamService.js`): `{ id, uuid, first_name, last_name, email, platform_role, access_mode,
 * status, invite_id, created_at }`. `status` excludes `deleted`/`invite_canceled` server-side and
 * is one of `active`/`inactive`/`blocked` ONLY — there is no `invited` status value anywhere in
 * this system (`users.status`'s DB default/enum is `active|inactive|deleted|blocked`; a pending
 * invite's user row just reads `inactive`, same as a genuinely deactivated member). `invite_id` is
 * the actual signal for "this row has a pending invite" — the matching `invites` row's own id when
 * one is pending, `null` otherwise. Members/index.js derives its own "Invited" display status from
 * `invite_id`, never from `status`.
 *
 * @param {object} [params]
 * @param {number} [params.page] 1-based page number.
 * @param {number} [params.pageSize] Rows per page.
 * @param {string} [params.searchKey] Case-insensitive match against first name, last name, or email.
 * @returns {Promise<{items: Array<object>, page: number, page_size: number, has_more?: boolean}>}
 */
export const getMembers = async ({ page = 1, pageSize = 25, searchKey } = {}) => {
  const res = await axios.get('/team/members', {
    ...NO_COMPANY_SCOPE_REQUEST_CONFIG,
    params: { page, page_size: pageSize, searchKey },
  });
  return res.data;
};
